import React from 'react';
import { motion } from 'framer-motion';
import { 
  FaGithub, FaLinkedin, FaInstagram, 
  FaEnvelope, FaMapMarkerAlt, FaPhone, FaGraduationCap 
} from 'react-icons/fa';
import { personalData } from '../data/portfolioData';
import { useTranslation } from 'react-i18next';
import { contentTranslations, langCode } from '../locales/contentTranslations';

const Hero = () => {
  const { t, i18n } = useTranslation();
  const L = langCode(i18n);
  const heroCopy = contentTranslations[L]?.hero;
  const desc = heroCopy?.description || personalData.description;
  const heroTitle = heroCopy?.title || personalData.title;
  const statsLabels = heroCopy?.stats || personalData.stats.map(stat => stat.label);
  const socialIcons = {
    //github: { icon: FaGithub, color: "#ffffff", name: "GitHub" },
    linkedin: { icon: FaLinkedin, color: "#0077b5", name: "LinkedIn" },
    instagram: { icon: FaInstagram, color: "#e4405f", name: "Instagram" },
  };

  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden py-12 md:py-0">
      {/* Animirana pozadina */}
      <div className="absolute inset-0 bg-gradient-to-br from-dark via-darker to-dark">
        <div className="absolute inset-0 opacity-30">
          <div className="absolute top-20 left-20 w-72 h-72 bg-primary rounded-full mix-blend-multiply filter blur-3xl animate-pulse"></div>
          <div className="absolute bottom-20 right-20 w-72 h-72 bg-secondary rounded-full mix-blend-multiply filter blur-3xl animate-pulse delay-1000"></div>
        </div>
      </div>

      <div className="relative container mx-auto px-4 sm:px-6 z-10">
        <div className="flex flex-col-reverse lg:flex-row items-center gap-8 lg:gap-12">
          
          {/* Leva strana - Tekst (na mobitelu ide ISPOD slike) */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            className="flex-1 text-center lg:text-left w-full"
          >
            <motion.div
              animate={{ scale: [1, 1.2, 1] }}
              transition={{ duration: 2, repeat: Infinity }}
              className="inline-block mb-4 md:mb-6"
            >
              <span className="px-3 py-1 md:px-4 md:py-2 glass-effect rounded-full text-xs md:text-sm">
                ✨ {t("hero.available")}
              </span>
            </motion.div>

            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-bold mb-3 md:mb-4">
              <span className="text-gradient">{t("hero.title")}</span>
              <br />
              <span className="text-white">{personalData.name}</span>
            </h1>

            <p className="text-base md:text-lg lg:text-xl text-gray-300 mb-3 md:mb-4">
              {heroTitle}
            </p>

            <p className="text-sm md:text-base lg:text-lg text-gray-400 max-w-2xl mx-auto lg:mx-0 mb-6 md:mb-8">
              {desc}
            </p>

            {/* Kontakt info - popravljeno poravnanje */}
            <div className="flex flex-col items-start gap-2 md:gap-3 mb-6 md:mb-8">
              <div className="flex items-start gap-3 text-gray-300 text-sm md:text-base">
                <FaEnvelope className="text-primary flex-shrink-0 mt-0.5" size={16} />
                <a href={`mailto:${personalData.email}`} className="hover:text-primary transition break-all">
                  {personalData.email}
                </a>
              </div>
              <div className="flex items-start gap-3 text-gray-300 text-sm md:text-base">
                <FaPhone className="text-primary flex-shrink-0 mt-0.5" size={16} />
                <a href={`tel:${personalData.phone}`} className="hover:text-primary transition">
                  {personalData.phone}
                </a>
              </div>
              <div className="flex items-start gap-3 text-gray-300 text-sm md:text-base">
                <FaMapMarkerAlt className="text-primary flex-shrink-0 mt-0.5" size={16} />
                <span>{personalData.location}</span>
              </div>
              <div className="flex items-start gap-3 text-gray-300 text-sm md:text-base">
                <FaGraduationCap className="text-primary flex-shrink-0 mt-0.5" size={16} />
                <span>{t("hero.faculty")}</span>
              </div>
            </div>

            {/* CTA Dugmad */}
            <div className="flex flex-wrap gap-3 md:gap-4 justify-center lg:justify-start mb-8 md:mb-12">
              <motion.a
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                href="#contact"
                className="px-5 md:px-8 py-2 md:py-3 bg-gradient-to-r from-primary to-secondary rounded-full font-semibold text-white text-sm md:text-base hover-glow"
              >
                {t("hero.contactBtn")}
              </motion.a>
              
              <motion.a
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                href="#projects"
                className="px-5 md:px-8 py-2 md:py-3 glass-effect rounded-full font-semibold text-white text-sm md:text-base hover:bg-white/10"
              >
                {t("hero.workBtn")}
              </motion.a>
            </div>

            {/* Social media links */}
            <div className="flex flex-wrap gap-3 md:gap-4 justify-center lg:justify-start">
              {Object.entries(personalData.social).map(([platform, url]) => {
                const SocialIcon = socialIcons[platform]?.icon;
                const color = socialIcons[platform]?.color;
                if (!SocialIcon || !url) return null;
                
                return (
                  <motion.a
                    key={platform}
                    whileHover={{ y: -5, scale: 1.1 }}
                    href={url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="group relative"
                    title={socialIcons[platform].name}
                  >
                    <div className="absolute inset-0 rounded-full blur-md opacity-0 group-hover:opacity-100 transition-opacity"
                         style={{ backgroundColor: color }}></div>
                    <div className="relative w-9 h-9 md:w-12 md:h-12 glass-effect rounded-full flex items-center justify-center hover:shadow-xl transition-all">
                      <SocialIcon size={18} className="md:w-6 md:h-6" style={{ color }} />
                    </div>
                  </motion.a>
                );
              })}
            </div>
          </motion.div>

          {/* Desna strana - Profilna slika (na mobitelu ide PRVA) */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            className="flex-1 flex justify-center w-full"
          >
            <div className="relative">
              <div className="absolute inset-0 rounded-full bg-gradient-to-r from-primary to-secondary animate-pulse blur-xl opacity-50"></div>
              <div className="absolute inset-0 rounded-full bg-gradient-to-r from-primary to-secondary animate-spin-slow" 
                   style={{ animation: 'spin 10s linear infinite' }}></div>
              
              <div className="relative w-40 h-40 sm:w-56 sm:h-56 md:w-72 md:h-72 lg:w-80 lg:h-80 rounded-full overflow-hidden border-4 border-primary/50 shadow-2xl mx-auto">
                <img
                  src={personalData.profileImage}
                  alt={personalData.name}
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    e.target.src = `https://ui-avatars.com/api/?name=${personalData.name.replace(' ', '+')}&background=6366f1&color=fff&size=400&bold=true`;
                  }}
                />
              </div>
              
              {/* Floating elementi - sakriveni na malim ekranima */}
              <motion.div
                animate={{ y: [0, -10, 0] }}
                transition={{ duration: 3, repeat: Infinity }}
                className="absolute -top-2 -right-2 sm:-top-4 sm:-right-4 w-8 h-8 sm:w-12 sm:h-12 glass-effect rounded-full flex items-center justify-center hidden sm:flex"
              >
                <span className="text-sm sm:text-xl">🤖</span>
              </motion.div>
              <motion.div
                animate={{ y: [0, 10, 0] }}
                transition={{ duration: 4, repeat: Infinity, delay: 1 }}
                className="absolute -bottom-2 -left-2 sm:-bottom-4 sm:-left-4 w-6 h-6 sm:w-10 sm:h-10 glass-effect rounded-full flex items-center justify-center hidden sm:flex"
              >
                <span className="text-xs sm:text-base">🚀</span>
              </motion.div>
            </div>
          </motion.div>
        </div>

        {/* Stats - responzivne kartice */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4 md:gap-6 lg:gap-8 mt-12 md:mt-16 lg:mt-20">
          {personalData.stats.map((stat, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              className="text-center glass-effect rounded-xl md:rounded-2xl p-2 md:p-4"
            >
              <div className="text-lg sm:text-xl md:text-3xl lg:text-4xl font-bold text-gradient mb-1 md:mb-2">
                {stat.value}
              </div>
              <div className="text-gray-400 text-xs sm:text-sm">{statsLabels[index]}</div>
            </motion.div>
          ))}
        </div>
      </div>

      {/* Scroll indicator - sakriven na malim ekranima */}
      <motion.div
        animate={{ y: [0, 10, 0] }}
        transition={{ duration: 1.5, repeat: Infinity }}
        className="absolute bottom-4 sm:bottom-8 left-1/2 transform -translate-x-1/2 hidden sm:block"
      >
        <div className="w-5 h-8 sm:w-6 sm:h-10 border-2 border-gray-400 rounded-full flex justify-center">
          <div className="w-1 h-2 bg-gray-400 rounded-full mt-2 animate-pulse"></div>
        </div>
      </motion.div>
    </section>
  );
};

export default Hero;